import { supabase } from '../lib/supabase';

/**
 * Analytics Service for GA4 Event Tracking
 * Tracks catalog engagement and conversion events
 */
export const analyticsService = {
  /**
   * Track catalog view event
   * @param {string} catalogId - Catalog ID
   * @param {string} catalogTitle - Catalog title
   * @param {string} companyId - Company ID
   */
  trackCatalogView(catalogId, catalogTitle, companyId) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'catalog_view', {
      catalog_id: catalogId,
      catalog_title: catalogTitle,
      company_id: companyId,
      timestamp: new Date()?.toISOString()
    });
  },

  /**
   * Track page view event within catalog
   * @param {string} catalogId - Catalog ID
   * @param {number} pageNumber - Page number (1-indexed)
   * @param {string} companyId - Company ID
   */
  trackPageView(catalogId, pageNumber, companyId) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'page_view', {
      catalog_id: catalogId,
      page_number: pageNumber,
      company_id: companyId,
      timestamp: new Date()?.toISOString()
    });
  },

  /**
   * Track hotspot click event
   * @param {string} catalogId - Catalog ID
   * @param {number} pageNumber - Page number (1-indexed)
   * @param {string} hotspotType - Hotspot type (buy/link)
   * @param {string} hotspotId - Hotspot ID
   * @param {string} companyId - Company ID
   */
  async trackHotspotClick(catalogId, pageNumber, hotspotType, hotspotId, companyId) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'hotspot_click', {
      catalog_id: catalogId,
      page_number: pageNumber,
      hotspot_type: hotspotType,
      hotspot_id: hotspotId,
      company_id: companyId,
      timestamp: new Date()?.toISOString()
    });

    // Track engagement for email notifications
    try {
      await this.recordHotspotEngagement(catalogId, hotspotId, hotspotType, pageNumber);
    } catch (error) {
      console.error('Failed to record hotspot engagement:', error?.message);
    }
  },

  /**
   * Track buy button click event (e-commerce conversion)
   * @param {string} catalogId - Catalog ID
   * @param {number} pageNumber - Page number (1-indexed)
   * @param {string} hotspotId - Hotspot ID
   * @param {string} sku - Product SKU
   * @param {number} quantity - Product quantity
   * @param {string} companyId - Company ID
   */
  trackBuyClick(catalogId, pageNumber, hotspotId, sku, quantity, companyId) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'buy_click', {
      catalog_id: catalogId,
      page_number: pageNumber,
      hotspot_id: hotspotId,
      product_sku: sku,
      quantity: quantity,
      company_id: companyId,
      timestamp: new Date()?.toISOString()
    });
  },

  /**
   * Record hotspot engagement and send notification if threshold reached
   */
  async recordHotspotEngagement(catalogId, hotspotId, hotspotType, pageNumber) {
    try {
      // Get catalog to retrieve company_id
      const { data: catalog } = await supabase
        ?.from('catalogs')
        ?.select('company_id')
        ?.eq('id', catalogId)
        ?.single();

      if (!catalog?.company_id) {
        throw new Error('Catalog not found or missing company_id');
      }

      // Get or create engagement record
      const { data: existing } = await supabase
        ?.from('hotspot_engagement_notifications')
        ?.select('*')
        ?.eq('hotspot_id', hotspotId)
        ?.single();

      const newClickCount = (existing?.click_count || 0) + 1;
      const threshold = existing?.notification_threshold || 10;

      // Update or insert engagement record
      const { data: engagement, error } = await supabase
        ?.from('hotspot_engagement_notifications')
        ?.upsert({
          id: existing?.id,
          catalog_id: catalogId,
          hotspot_id: hotspotId,
          company_id: catalog?.company_id,
          click_count: newClickCount,
          notification_threshold: threshold,
          last_notified_at: existing?.last_notified_at
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Send notification if threshold reached and not notified recently
      const shouldNotify = newClickCount % threshold === 0;
      const lastNotified = existing?.last_notified_at ? new Date(existing.last_notified_at) : null;
      const hoursSinceLastNotification = lastNotified ? (Date.now() - lastNotified?.getTime()) / (1000 * 60 * 60) : Infinity;

      if (shouldNotify && hoursSinceLastNotification > 24) {
        await this.sendEngagementNotification(catalogId, hotspotId, hotspotType, pageNumber, newClickCount);
        
        // Update last notified timestamp
        await supabase
          ?.from('hotspot_engagement_notifications')
          ?.update({ last_notified_at: new Date()?.toISOString() })
          ?.eq('id', engagement?.id);
      }
    } catch (error) {
      console.error('Error recording hotspot engagement:', error?.message);
      throw error;
    }
  },

  /**
   * Send engagement notification email
   */
  async sendEngagementNotification(catalogId, hotspotId, hotspotType, pageNumber, clickCount) {
    try {
      // Get catalog and hotspot details
      const [catalog, hotspot] = await Promise.all([
        supabase?.from('catalogs')?.select('*, user_profiles!catalogs_created_by_fkey(email, full_name)')?.eq('id', catalogId)?.single(),
        supabase?.from('hotspots')?.select('*')?.eq('id', hotspotId)?.single()
      ]);

      if (!catalog?.data || !hotspot?.data) return;

      const { emailService } = await import('./emailService');
      const analyticsUrl = `${window.location?.origin}/analytics-dashboard?catalog=${catalogId}`;

      await emailService?.sendHotspotEngagement({
        ownerEmail: catalog?.data?.user_profiles?.email,
        ownerName: catalog?.data?.user_profiles?.full_name || 'there',
        catalogTitle: catalog?.data?.title,
        hotspotTitle: hotspot?.data?.title || 'Untitled Hotspot',
        hotspotType: hotspotType,
        pageNumber: pageNumber,
        clickCount: clickCount,
        analyticsUrl: analyticsUrl
      });
    } catch (error) {
      console.error('Failed to send engagement notification:', error?.message);
    }
  },

  /**
   * Track share event
   * @param {string} catalogId - Catalog ID
   * @param {string} shareMethod - Share method (email/link/social)
   * @param {string} companyId - Company ID
   */
  trackShare(catalogId, shareMethod, companyId) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'share', {
      catalog_id: catalogId,
      share_method: shareMethod,
      company_id: companyId,
      timestamp: new Date()?.toISOString()
    });
  },

  /**
   * Track embed view event
   * @param {string} catalogId - Catalog ID
   * @param {string} embedDomain - Domain where catalog is embedded
   * @param {string} companyId - Company ID
   */
  trackEmbedView(catalogId, embedDomain, companyId) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'embed_view', {
      catalog_id: catalogId,
      embed_domain: embedDomain,
      company_id: companyId,
      timestamp: new Date()?.toISOString()
    });
  }
};